<template>
  <div class='event-bracket'>
    <div id='interact-bracket' style='touch-action: none;'>
      <v-container ref='bracket'>
        <v-row>
          <bracket :rounds='bracketData'>
            <template v-slot:player='{player}'>
              <bracket-item :player='player' />
            </template>
          </bracket>
        </v-row>
      </v-container>
    </div>
    <v-btn class='refresh-btn' depressed color='deep-purple' dark @click="$emit('refresh')">REFRESH<v-icon style='padding-left: 10px'>fas fa-redo</v-icon></v-btn>
  </div>
</template>

<script>
import interact from 'interactjs'
import Bracket from 'vue-tournament-bracket'
import BracketItem from '@/components/TPC/Event/BracketItem'

const solveTimeToPlayer = (solveTime, score, index, matchupData) => {
  let diff = score[index] - score[(index + 1) % 2]
  return {
    id: solveTime.profileID,
    name: solveTime.solverName,
    wcaID: solveTime.wcaID,
    seed: solveTime.seed,
    score: score[index],
    winner: diff === 0 ? null : diff > 0,
    matchupData
  }
}

const getGameScore = (times1, times2) => {
  let i = 0
  let score = [0, 0]
  while (i < times1.length && i < times2.length) {
    let time1 = times1[i].time
    let time2 = times2[i].time
    if (times1[i].metadata !== 'ok' && times1[i].metadata !== '+2') time1 = Infinity
    if (times2[i].metadata !== 'ok' && times2[i].metadata !== '+2') time2 = Infinity
    score[0] += time1 <= time2
    score[1] += time2 <= time1

    i++
  }
  score[0] += times1.length - i
  score[1] += times2.length - i

  if (times1.length === 0) score[0] = null
  if (times2.length === 0) score[1] = null
  return score
}

export default {
  name: 'event-bracket',
  components: {
    Bracket,
    BracketItem,
  },
  data () {
    return {
      oldTransform: {
        scale: 1,
      },
      currentTransform: {
        scale: 1,
        translateX: 0,
        translateY: 0,
      }
    }
  },
  props: {
    rounds: Array
  },
  methods: {
    handleDrag(event) {
      let target = event.target
      this.currentTransform.translateX += event.dx
      this.currentTransform.translateY += event.dy

      // translate the element
      this.applyCurrentTransform(target)
    },
    applyCurrentTransform (target) {
      const {translateX, translateY, scale} = this.currentTransform

      target.style.webkitTransform =
        target.style.transform =
          'translate(' + translateX + 'px, ' + translateY + 'px)' + 'scale(' + scale + ')'
    },
    scaleHandler ({type, key}) {
      if (type !== 'keydown') return
      if (key === '-') return this.scaleDown()
      if (key === '=') return this.resetScale()
    },
    scaleDown () {
      this.$refs.bracket.style.transform = `scale(0.5)`
    },
    resetScale () {
      this.$refs.bracket.style.transform = `scale(1.0)`
    }
  },
  computed: {
    bracketData () {
      // Don't render bracket until we have the full data (otherwise vue-tournament-bracket throws an error because we don't have an even bracket)
      if (!(this.rounds.reduce((acc, round) => (acc && round && round.solveTimes), true))) return []
      return this.rounds.map((round) => {
        let solveTimes = []
        for (let [key, value] of Object.entries(round.solveTimes)) {
          solveTimes[value.solverOrder] = {
            ...value,
            roundID: round.roundID,
            profileID: key
          }
        }
        let games = []
        const _BYE = { id: 'bye', name: '--BYE--', winner: false, score: null }
        for (let i = 0; i < solveTimes.length; i += 2) {
          const score = getGameScore(solveTimes[i] ? solveTimes[i].times : [], solveTimes[i + 1] ? solveTimes[i + 1].times : [])
          const matchupData = [solveTimes[i], solveTimes[i+1]]
          games.push({
            player1: !solveTimes[i] ? _BYE : solveTimeToPlayer(solveTimes[i], score, 0, matchupData),
            player2: !solveTimes[i + 1] ? _BYE : solveTimeToPlayer(solveTimes[i + 1], score, 1, matchupData)
          })
        }
        
        const _EMPTY = { id: 'empty', name: '-', winner: null, score: null }
        // fill rest of the games with empty (indicating that these matchups either were empty or bracket hasn't yet been set)
        const regexmatch = round.roundID.match(/.*R(\d+)/)
        if (regexmatch && !Object.is(parseInt(regexmatch[1]), NaN)) {
          const totalMatches = parseInt(regexmatch[1])/2
          if (games.length < totalMatches) games = games.concat(new Array(totalMatches - games.length).fill({player1: _EMPTY, player2: _EMPTY}))
        }

        return {games}
      })
    }
  },
  mounted() {
    window.addEventListener('keydown', this.scaleHandler)

    var self = this
    interact('#interact-bracket')
      .draggable({
        //inertia: true,
        onmove: this.handleDrag
      })
      .gesturable({
        listeners: {
          move (event) {
            self.currentTransform.scale = Math.pow(event.scale, 0.5) * self.oldTransform.scale

            self.handleDrag(event) // this will handle the drag as well as apply the current transform.
          },
          end (event) {
            self.oldTransform.scale *= Math.pow(event.scale, 0.5)
          }
        }
      })
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.scaleHandler)
  }
}
</script>

<style scoped>
.event-bracket {
  overflow: hidden;
  min-height: 100vh;
}
.container {
  transform-origin: center left;
}

::v-deep .vtb-item-players {
  width: 270px;
  max-width: 45vw;
  color: black;
  outline: 2px solid black;
}

::v-deep .vtb-item-players .defeated {
  background-color: rgba(255, 0, 0, 0.568);
}
::v-deep .vtb-item-players .defeated.highlight {
  background-color: rgba(255, 8, 0, 0.65);
}
::v-deep .vtb-item-players .winner {
  background-color: rgba(0, 255, 0, 0.4);
}
::v-deep .vtb-item-players .winner.highlight {
  background-color: rgba(51, 255, 0, 0.507);
}

::v-deep .vtb-item-players .vtb-player {
  padding: 0;
}
::v-deep .vtb-item-players {
  background-color: rgba(144, 144, 144, 0.5);
}
::v-deep .vtb-item-child {
  margin-top: 5px;
  margin-bottom: 5px;
}

.refresh-btn {
  position: absolute;
  right: 20vw;
  top: 2vh;
}
</style>
